import { useEffect, useState } from "react";
import axios from "axios";
import { Clock, CheckCircle2 } from "lucide-react";
import { API } from "@/lib/api";

/**
 * TenantTrialBanner — small pill visible in the admin header when the
 * current user's tenant has a live trial. Shows N days left; goes
 * emerald when active, amber when <=3 days, rose when expired.
 *
 * Hidden entirely for the flagship tenant (Link Ride's own operator) so
 * the platform owner doesn't see a countdown to themselves.
 */
export default function TenantTrialBanner() {
  const [tenant, setTenant] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/me/tenant`)
      .then((r) => {
        if (!cancelled) setTenant(r.data || null);
      })
      .catch(() => {
        if (!cancelled) setTenant(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!tenant || tenant.is_flagship) return null;
  if (!tenant.trial_expires_at) return null;

  const days = tenant.trial_days_left;
  const expired = days === 0;
  const urgent = days !== null && days <= 3 && !expired;

  const palette = expired
    ? {
        wrap: "border-rose-500/40 bg-rose-500/10 text-rose-100",
        icon: "text-rose-300",
        label: "Trial ended",
        sub: "Contact Link Ride Ops to convert.",
      }
    : urgent
    ? {
        wrap: "border-amber-500/40 bg-amber-500/10 text-amber-100",
        icon: "text-amber-300",
        label: `${days} day${days === 1 ? "" : "s"} left on your trial`,
        sub: `Fee kicks in at ${tenant.platform_fee_pct}% of completed rides after.`,
      }
    : {
        wrap: "border-emerald-500/30 bg-emerald-500/8 text-emerald-100",
        icon: "text-emerald-300",
        label: `${days} day${days === 1 ? "" : "s"} left on your trial`,
        sub: `Fee kicks in at ${tenant.platform_fee_pct}% of completed rides after.`,
      };

  const Icon = expired ? CheckCircle2 : Clock;

  return (
    <div
      className={`rounded-xl border ${palette.wrap} px-4 py-3 flex items-center gap-3`}
      data-testid="tenant-trial-banner"
    >
      <Icon className={`w-5 h-5 ${palette.icon} flex-shrink-0`} />
      <div className="text-sm leading-tight">
        <p className="font-bold uppercase tracking-wider text-xs">{palette.label}</p>
        <p className="text-xs opacity-80 mt-0.5">{palette.sub}</p>
      </div>
    </div>
  );
}
